import { useEffect, useState } from "react";
import {
  api_private_get,
  formatStringAmountCredits,
  formatUnixToLabel,
} from "script";
import CashOutModal from "../../../components/CashOutModal";
import "../../../components/HoldingsTable.css";
import useAuth from "context/useAuth";
import {
  formatOrderStatus,
  getDisplayedStake,
} from "../../../utils/holdingsDisplay";

function formatExpectedWin(amount, odds) {
  if (odds === 0) return "N/A";
  const win = Math.floor(amount * odds * 100) / 100;
  return formatStringAmountCredits(win.toFixed(2));
}

function calculatePNL(amount, odds, lastPrice) {
  if (!amount || !lastPrice) {
    return {
      value: 0,
      pnl: 0,
      pnlPercent: 0,
      sign: "",
      isValid: false,
    };
  }

  const value = (amount * odds) / lastPrice;
  const pnl = value - amount;
  const pnlPercent = (pnl / amount) * 100;
  const sign = pnl >= 0 ? "+" : "";

  return {
    value,
    pnl,
    pnlPercent,
    sign,
    isValid: true,
  };
}

// function formatContractValue(amount, odds, lastPrice) {
//     if (lastPrice === 0) return 'N/A';
//     if (odds === lastPrice) return formatStringAmountCredits(amount);
//     const value = Math.floor((amount * odds / lastPrice) * 100) / 100;
//     return formatStringAmountCredits(value.toFixed(2));
// }

function HoldingPNL({ order, showHistory = false }) {
  if (showHistory)
    return (
      <td data-label="Unrealized P&L" className="text-nowrap">
        &mdash;
      </td>
    );

  const amount = getDisplayedStake(order);
  const odds = order.order_odds;
  const lastPrice = order.product.last_price;

  const { pnl, pnlPercent, sign, isValid } = calculatePNL(
    amount,
    odds,
    lastPrice,
  );

  if (!isValid)
    return (
      <td data-label="Unrealized P&L" className="text-nowrap holdings-negative">
        Error
      </td>
    );

  return (
    <td
      data-label="Unrealized P&L"
      className={`text-nowrap ${pnl >= 0 ? "holdings-positive" : "holdings-negative"}`}
    >
      {sign}
      {formatStringAmountCredits(pnl.toFixed(2))} ({sign}
      {pnlPercent.toFixed(2)}%)
    </td>
  );
}

function OrderRow({ order, showStatus = false, onCashOut }) {
  const [isOpen, setIsOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const toggleOpen = () => setIsOpen((prev) => !prev);
  const handleRowKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleOpen();
    }
  };
  const toggleDetails = (e) => {
    e.stopPropagation();
    setShowDetails((prev) => !prev);
  };

  const amount = getDisplayedStake(order, showStatus);
  const odds = order.order_odds;
  const lastPrice = order.product.last_price;
  const direction = order.product.direction;
  const directionLabel = direction === "OVER" ? "Above" : "Below";
  const directionIcon = direction === "OVER" ? "ti-arrow-up" : "ti-arrow-down";
  const detailsId = `holding-details-${order.order_id}`;

  const { pnl, pnlPercent, sign, isValid } = calculatePNL(
    amount,
    odds,
    lastPrice,
  );

  const handleCashOut = (e) => {
    e.stopPropagation();
    if (onCashOut) {
      onCashOut(order);
    }
  };

  return (
    <>
      <tr
        key={order.order_id}
        onClick={toggleOpen}
        onKeyDown={handleRowKeyDown}
        className={`cpoint holding-summary-row ${isOpen ? "is-expanded" : ""}`}
        tabIndex="0"
        aria-expanded={isOpen}
        aria-controls={detailsId}
      >
        <td data-label="Order ID" className="text-nowrap font-monospace">
          <span
            className={`holdings-toggle-icon ${isOpen ? "open" : "closed"}`}
            aria-hidden="true"
          >
            <i className="ti ti-chevron-right"></i>
          </span>
          <span className="ms-2">{order.order_id.toUpperCase()}</span>
        </td>
        <td data-label="Contract" className="text-nowrap">
          {order.product_id}
        </td>
        <td data-label="Stake" className="text-nowrap">
          {formatStringAmountCredits(amount)}
        </td>
        <td data-label="Payout" className="text-nowrap">
          {formatExpectedWin(amount, order.order_odds)} (x{order.order_odds})
        </td>
        <HoldingPNL order={order} showHistory={showStatus} />
        {showStatus && (
          <td data-label="Status" className="text-nowrap">
            {formatOrderStatus(order.status)}
          </td>
        )}
        <td data-label="Expiration Date" className="text-nowrap fw-normal">
          {showStatus
            ? new Date(order.order_date).toLocaleString()
            : formatUnixToLabel(
                new Date(order.product.maturity_date).getTime() / 1000,
              )}
        </td>
      </tr>

      {isOpen && (
        <tr className="table-active" id={detailsId}>
          <td colSpan={showStatus ? 7 : 6} className="holding-card-cell">
            <div className="holding-card">
              <div className="holding-card-header">
                <div className="holding-card-heading">
                  <span
                    className={`holding-direction holding-direction-${direction?.toLowerCase()}`}
                    aria-hidden="true"
                  >
                    <i className={`ti ${directionIcon}`}></i>
                  </span>
                  <div>
                    <div className="holding-card-eyebrow">
                      {showStatus ? "Completed position" : "Active position"}
                    </div>
                    <div className="holding-card-title">
                      {order.product.currency} {directionLabel}{" "}
                      {Number(order.product.strike).toLocaleString()}
                    </div>
                    <div className="holding-card-contract">
                      {order.product_id}
                    </div>
                  </div>
                </div>
                <div className="holding-card-quote">
                  <span>Entry odds</span>
                  <strong>x{order.order_odds}</strong>
                </div>
              </div>

              <div className="holding-card-grid">
                <div className="holding-card-item">
                  <div className="holding-card-icon" aria-hidden="true">
                    <i className="ti ti-wallet"></i>
                  </div>
                  <div className="holding-card-label">
                    {showStatus ? "Original Stake" : "Remaining Stake"}
                  </div>
                  <div className="holding-card-value">
                    {formatStringAmountCredits(amount)}
                  </div>
                </div>

                <div className="holding-card-item">
                  <div className="holding-card-icon" aria-hidden="true">
                    <i className="ti ti-coins"></i>
                  </div>
                  <div className="holding-card-label">Potential Payout</div>
                  <div className="holding-card-value">
                    {formatExpectedWin(amount, order.order_odds)}
                  </div>
                </div>

                <div className="holding-card-item">
                  <div className="holding-card-icon" aria-hidden="true">
                    <i className="ti ti-chart-line"></i>
                  </div>
                  <div className="holding-card-label">Unrealized P&amp;L</div>
                  <div
                    className={`holding-card-value ${showStatus || !isValid ? "" : pnl >= 0 ? "holdings-positive" : "holdings-negative"}`}
                  >
                    {showStatus
                      ? "—"
                      : !isValid
                        ? "Error"
                        : `${sign}${formatStringAmountCredits(pnl.toFixed(2))} (${sign}${pnlPercent.toFixed(2)}%)`}
                  </div>
                </div>

                <div className="holding-card-item">
                  <div className="holding-card-icon" aria-hidden="true">
                    <i className="ti ti-clock-hour-4"></i>
                  </div>
                  <div className="holding-card-label">Expiration</div>
                  <div className="holding-card-value">
                    {formatUnixToLabel(
                      new Date(order.product.maturity_date).getTime() / 1000,
                    )}
                  </div>
                </div>
              </div>

              {showDetails && (
                <div className="holding-details">
                  <div className="holding-details-heading">Position details</div>
                  <div className="holding-details-grid">
                    <div className="holding-details-item">
                      <span className="holding-details-key">Order placed</span>
                      <span className="holding-details-value">
                        {new Date(order.order_date).toLocaleString()}
                      </span>
                    </div>
                    <div className="holding-details-item">
                      <span className="holding-details-key">Direction</span>
                      <span className="holding-details-value">
                        {directionLabel} {Number(order.product.strike).toLocaleString()}
                      </span>
                    </div>
                    <div className="holding-details-item">
                      <span className="holding-details-key">Current odds</span>
                      <span className="holding-details-value">
                        {lastPrice ? `x${lastPrice}` : "Unavailable"}
                      </span>
                    </div>
                    <div className="holding-details-item">
                      <span className="holding-details-key">Status</span>
                      <span className="holding-details-value">
                        {formatOrderStatus(order.status)}
                      </span>
                    </div>
                    <div className="holding-details-item holding-details-item-wide">
                      <span className="holding-details-key">Order ID</span>
                      <span className="holding-details-value">
                        {order.order_id.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="holding-card-footer">
                <div className="holding-card-status">
                  <span
                    className={`holding-card-status-badge ${order.status === "open" ? "active" : order.status}`}
                  >
                    {formatOrderStatus(order.status)}
                  </span>
                  <span className="holding-card-id">
                    #{order.order_id.toUpperCase()}
                  </span>
                </div>
                <div className="holding-card-actions">
                  <button
                    type="button"
                    className="holding-card-details-btn"
                    onClick={toggleDetails}
                    aria-expanded={showDetails}
                  >
                    <i
                      className={`ti ${showDetails ? "ti-chevron-up" : "ti-list-details"}`}
                      aria-hidden="true"
                    ></i>
                    {showDetails ? "Hide details" : "View details"}
                  </button>
                  {order.status === "open" && (
                    <button
                      type="button"
                      className="holding-card-cashout-btn"
                      onClick={handleCashOut}
                    >
                      Cash out
                      <i className="ti ti-arrow-right" aria-hidden="true"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function HoldingsTab() {
  const [accountOrdersData, setAccountOrdersData] = useState(null);
  const [activeTab, setActiveTab] = useState("my-holdings");
  const [cashOutOrder, setCashOutOrder] = useState(null);
  const { refreshUser } = useAuth();

  const fetchOrdersData = () => {
    api_private_get("/account/get_orders", (data) => {
      console.log("get_orders data:", data);
      setAccountOrdersData(data);
    });
  };

  useEffect(() => {
    fetchOrdersData();
  }, []);

  const openOrders = Array.isArray(accountOrdersData)
    ? accountOrdersData.filter((order) => order.status === "open")
    : [];

  const closedOrders = Array.isArray(accountOrdersData)
    ? accountOrdersData.filter((order) => order.status !== "open")
    : [];

  const handleCashOut = (order) => {
    setCashOutOrder(order);
  };

  const handleCashOutSuccess = () => {
    fetchOrdersData();
    void refreshUser();
  };

  return (
    <div>
      {cashOutOrder && (
        <CashOutModal
          order={cashOutOrder}
          onClose={() => setCashOutOrder(null)}
          onSuccess={handleCashOutSuccess}
        />
      )}

      <div className="pay_method__tabletwo holdings-table-container">
        {/* Tab Navigation */}
        <div className="holdings-tabs">
          <button
            onClick={() => setActiveTab("my-holdings")}
            className={`holdings-tab ${activeTab === "my-holdings" ? "active" : ""}`}
          >
            My Holdings
          </button>
          <button
            onClick={() => setActiveTab("holdings-history")}
            className={`holdings-tab ${activeTab === "holdings-history" ? "active" : ""}`}
          >
            Holdings History
          </button>
        </div>

        {/* My Holdings Tab */}
        {activeTab === "my-holdings" && (
          <div className="pay_method__table-scrollbar">
            <table className="w-100 text-center p2-bg" id="holdings-table">
              <thead>
                <tr>
                  <th className="text-nowrap">Order ID</th>
                  <th className="text-nowrap">Contract</th>
                  <th className="text-nowrap">Stake</th>
                  <th className="text-nowrap">Payout</th>
                  <th className="text-nowrap">Unrealized P&amp;L</th>
                  <th className="text-nowrap">Expiration Date</th>
                </tr>
              </thead>
              <tbody>
                {openOrders.length === 0 && (
                  <tr>
                    <td
                      colSpan="6"
                      className="text-center holdings-empty-state"
                    >
                      There are no holdings in your account for the moment...
                    </td>
                  </tr>
                )}
                {openOrders.map((order) => (
                  <OrderRow
                    key={order.order_id}
                    order={order}
                    showStatus={false}
                    onCashOut={handleCashOut}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Holdings History Tab */}
        {activeTab === "holdings-history" && (
          <div className="pay_method__table-scrollbar">
            <table
              className="w-100 text-center p2-bg"
              id="holdings-history-table"
            >
              <thead>
                <tr>
                  <th className="text-nowrap">Order ID</th>
                  <th className="text-nowrap">Contract</th>
                  <th className="text-nowrap">Stake</th>
                  <th className="text-nowrap">Payout</th>
                  <th className="text-nowrap">Unrealized P&L</th>
                  <th className="text-nowrap">Status</th>
                  <th className="text-nowrap">Order Date</th>
                </tr>
              </thead>
              <tbody>
                {closedOrders.length === 0 && (
                  <tr>
                    <td
                      colSpan="7"
                      className="text-center holdings-empty-state"
                    >
                      You have no order history yet...
                    </td>
                  </tr>
                )}
                {closedOrders.map((order) => (
                  <OrderRow
                    key={order.order_id}
                    order={order}
                    showStatus={true}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
